/**
 * Convert ANY error coming from axios / fetch / window.Error into a
 * human-readable string suitable for `toast.error(...)`.
 *
 * Why this helper exists: across the app we had calls like
 *   toast.error(err?.response?.data?.detail || "fallback")
 *   toast.error(String(err?.response?.data?.detail || "fallback"))
 *   toast.error(msg)   // where msg = (await res.json()).detail
 *
 * Those all break the same way: FastAPI / Pydantic v2 returns
 * validation errors as `{"detail": [{"loc": [...], "msg": "...",
 * "type": "..."}, ...]}` — an ARRAY OF OBJECTS, not a string. When
 * you String() an array of objects you get `"[object Object]"` —
 * exactly the unhelpful toast the user reported on the Refine flow.
 *
 * Contract:
 *   - String  → returned verbatim
 *   - Array of Pydantic 422 entries → "field: msg" lines joined with " · "
 *   - { detail: ... } unwrapped recursively
 *   - axios error → unwrap to response.data, then recurse
 *   - Plain Error → .message
 *   - Falsy or anything else → fallback string
 *
 * @param {unknown} err     The error / response payload / message.
 * @param {string} fallback Shown when nothing useful can be extracted.
 * @returns {string}        Always a non-empty string.
 */
export function formatApiError(err, fallback = "Something went wrong.") {
  if (err == null) return fallback;
  if (typeof err === "string") {
    const s = err.trim();
    if (!s) return fallback;
    // Cloudflare / nginx error pages come back as HTML. Rather than
    // dumping the raw markup into a toast — the exact "The origin
    // web server returned an invalid or incomplete response to
    // Cloudflare…" wall of text designers complained about — detect
    // the canonical Cloudflare error codes / signature phrases and
    // translate to a one-line, action-oriented message.
    //
    // We match on BOTH the numeric code AND the signature phrase, so
    // any of Cloudflare's error-page templates (the old one that shows
    // "Error 520", the newer ones that only render the tagline) get
    // caught.
    if (s.startsWith("<") || /<html/i.test(s) || /<!doctype/i.test(s)) {
      // 520 — origin returned invalid / empty response (most common
      // during pod restarts).
      if (
        /error\s*52[0-37]/i.test(s)
        || /returned an invalid or incomplete response/i.test(s)
        || /web server is returning an unknown error/i.test(s)
      ) {
        return "Network hiccup — the server briefly returned an invalid response. Please try again in a few seconds.";
      }
      // 524 — origin took too long to respond.
      if (
        /error\s*524/i.test(s)
        || /a timeout occurred/i.test(s)
        || /origin web server timed out/i.test(s)
      ) {
        return "The server took too long to respond. Please try again — most jobs work on the second attempt.";
      }
      // 502 / 503 / 504 — upstream unavailable.
      if (
        /error\s*50[234]/i.test(s)
        || /bad gateway/i.test(s)
        || /service unavailable/i.test(s)
        || /gateway time-?out/i.test(s)
      ) {
        return "The server is briefly unavailable. Please try again in a few seconds.";
      }
      // Any other HTML — return a friendly generic message; the raw
      // markup is useless to designers. Prefer the caller's fallback,
      // but if that's empty (nested recursion) surface a sensible
      // default so nothing ever falls through as HTML.
      return fallback || "Something went wrong. Please try again in a few seconds.";
    }
    return s;
  }

  // axios error → unwrap to the server response body if present, else
  // fall through to `.message`. CRITICAL ordering: response.data first
  // (the server's view of the failure) before err.message (axios's
  // generic "Network Error" / "Request failed with status 422").
  if (err.isAxiosError || err.response) {
    const data = err.response?.data;
    if (data != null) {
      const out = formatApiError(data, "");
      if (out) return out;
    }
    if (err.message) return err.message;
    return fallback;
  }

  // Plain JS Error.
  if (err instanceof Error) return err.message || fallback;

  // Pydantic v2 422: { detail: [{ loc, msg, type }, ...] }
  // FastAPI custom error: { detail: "string" }
  // Some endpoints return { detail: { ... } } — recurse one level.
  if (typeof err === "object") {
    if ("detail" in err) {
      const d = err.detail;
      if (typeof d === "string") return d.trim() || fallback;
      if (Array.isArray(d)) {
        const parts = d
          .map((e) => {
            if (typeof e === "string") return e;
            if (e && typeof e === "object") {
              // Pydantic shape: { loc: ["body", "field", ...], msg, type }
              const where = Array.isArray(e.loc)
                ? e.loc.filter((s) => s !== "body").join(".")
                : "";
              const msg = e.msg || e.message || "";
              if (where && msg) return `${where}: ${msg}`;
              if (msg) return msg;
            }
            return null;
          })
          .filter(Boolean);
        if (parts.length) return parts.join(" · ");
      }
      if (d && typeof d === "object") {
        return formatApiError(d, fallback);
      }
    }
    // Common alt keys.
    for (const k of ["message", "error", "msg", "error_message"]) {
      const v = err[k];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
  }

  return fallback;
}
